import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import Sidebar from "../../../components/SideBar";
import Navbar from "../../../components/NavBar";
import { getAllPatientsForAdmin } from "../../../../service-layer/PatientAdminService";
import DeactivatePatient from "../Components/DeactivatePatient/DeactivatePatient";
import PatientDetailsModal from "../Components/PatientDetailsModal/PatientDetailsModal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClipLoader from "react-spinners/ClipLoader";
import { format } from "date-fns";
import LogoutModal from "../../../components/logout-moal/LogoutModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye } from "react-icons/fa";

export const AllPatientsTable = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [tableHeight, setTableHeight] = useState(400);
  const navigate = useNavigate();
  const gridRef = useRef(null);

  // Calculate table height based on window size
  useEffect(() => {
    const calculateTableHeight = () => {
      // Get window height
      const windowHeight = window.innerHeight;
      // Subtract navbar (64px), header (80px), and padding (40px)
      const availableHeight = windowHeight - 64 - 80 - 40;
      // Minimum height of 300px, maximum of available space
      const newHeight = Math.max(300, Math.min(availableHeight, 800));
      setTableHeight(newHeight);
    };

    calculateTableHeight();
    window.addEventListener('resize', calculateTableHeight);
    
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, []);

  // Column Definitions
  const [colDefs, setColDefs] = useState([]);

  useEffect(() => {
    if (window.localStorage.getItem("auth_token") === null) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const columns = [
      {
        field: "surname",
        headerName: t("table.surname"),
        filter: true,
        floatingFilter: true,
        flex: 1,
      },
      {
        field: "name",
        headerName: t("table.name"),
        filter: true,
        floatingFilter: true,
        flex: 1,
      },
      {
        field: "fiscalCode",
        headerName: t("table.fiscalCode"),
        filter: true,
        floatingFilter: true,
        flex: 1,
      },
      {
        field: "active",
        headerName: t("activeStatus"),
        filter: true,
        floatingFilter: true,
        flex: 0.8,
        cellRenderer: (params) => {
          const isActive = params.value;
          return (
            <span
              style={{
                color: isActive ? "#28a745" : "#dc3545",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              {isActive ? t("active") : t("inactive")}
            </span>
          );
        },
      },
      {
        field: "actions",
        headerName: t("actions"),
        flex: 0.8,
        cellRenderer: (params) => (
          <div style={{ marginTop: "0px" }}>
            <DeactivatePatient 
              userId={params.data.userId}
              isActive={params.data.active}
              patientName={`${params.data.name} ${params.data.surname}`}
              onStatusChange={() => loadPatients()}
            />
          </div>
        ),
        sortable: false,
        filter: false,
      },
      {
        field: "details",
        headerName: t("details"),
        flex: 0.6,
        cellRenderer: (params) => (
          <div 
            style={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center", 
              height: "100%",
              cursor: "pointer"
            }}
            onClick={() => handleViewDetails(params.data)}
          >
            <FaEye
              style={{
                color: "#204770",
                fontSize: "1.2rem",
                transition: "color 0.3s ease",
              }}
              onMouseEnter={(e) => e.target.style.color = "#286F9E"}
              onMouseLeave={(e) => e.target.style.color = "#204770"}
            />
          </div>
        ),
        sortable: false,
        filter: false,
      },
    ];

    setColDefs(columns);
  }, [i18n.language, t]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.sizeColumnsToFit();
    }
  }, [gridRef]);

  const onFirstDataRendered = (params) => {
    params.api.sizeColumnsToFit();
  };

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setShowPatientDetails(true);
  };

  const handleCloseDetails = () => {
    setSelectedPatient(null);
    setShowPatientDetails(false);
  };

  const loadPatients = () => {
    getAllPatientsForAdmin()
      .then((response) => {
        console.log("All Patients Data:", response.data);
        setPatientData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading patients:", error);
        setShowLogoutModal(true);
      });
  };

  useEffect(() => {
    loadPatients();
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      minWidth: 100,
      resizable: true,
      sortable: true,
    };
  }, []);

  const pagination = true;
  const paginationPageSize = 20;
  const paginationPageSizeSelector = [10, 20, 50];

  const handleConfirm = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("name");
    localStorage.removeItem("surname");
    localStorage.removeItem("authority");
    setShowLogoutModal(false);
    navigate("/");
  };

  const token = window.localStorage.getItem("auth_token");

  return (
    <>
    <ToastContainer position="top-center" />
      {token && (
        <div className="dashboard d-flex">
          <div>
            <Sidebar />
          </div>
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              flexFlow: "column",
              height: "100vh",
              overflowY: "hidden",
            }}
          >
            <Navbar />
            <div style={{ 
              display: "flex", 
              flexDirection: "column", 
              height: "calc(100vh - 64px)",
              overflow: "hidden"
            }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "20px",
                  borderBottom: "1px solid #ddd",
                  minHeight: "80px",
                  flexShrink: 0,
                }}
              >
                <h2>{t("allPatientsManagement")}</h2>
                {!loading && (
                  <span style={{ fontSize: "1.1rem" }}>
                    {t("totalPatients")}: <strong>{patientData.length}</strong>
                  </span>
                )}
              </div>
              
              {/* Table Container */}
              <div style={{ 
                flex: 1, 
                padding: "20px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
              }}>
                {patientData.length > 0 && (
                    <div
                    className={"ag-theme-alpine"}
                    style={{
                        height: tableHeight,
                        padding: "20px",
                    }}
                    >
                    {!loading && (
                        <AgGridReact
                        ref={gridRef}
                        rowData={patientData}
                        columnDefs={colDefs}
                        onFirstDataRendered={onFirstDataRendered}
                        defaultColDef={defaultColDef}
                        pagination={pagination}
                        paginationPageSize={paginationPageSize}
                        paginationPageSizeSelector={paginationPageSizeSelector}
                        suppressHorizontalScroll={false}
                        enableRangeSelection={true}
                        animateRows={true}
                        />
                    )}

                    {loading && (
                        <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "10px",
                            backgroundColor: "white",
                            height: "100%",
                            width: "100%",
                        }}
                        >
                        <ClipLoader
                            color="#204770"
                            loading={loading}
                            size={100}
                            aria-label="Loading Spinner"
                            data-testid="loader"
                        />
                        </div>
                    )}
                    </div>
                )}
              </div>
              
              {patientData.length === 0 && !loading && (
                <div
                  style={{
                    height: "90vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <h3>{t("noPatientsFound")}</h3>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Patient Details Modal */}
      {selectedPatient && (
        <PatientDetailsModal
          isOpen={showPatientDetails}
          onClose={handleCloseDetails}
          patient={selectedPatient}
        />
      )}

      {!token && <div className="login-container"></div>}
      <LogoutModal
        show={showLogoutModal}
        handleConfirm={handleConfirm}
        handleClose={handleConfirm}
        title={t("login")}
        message={t("session_expired_please_login_again")}
        tag={"Login"}
      />
    </>
  );
};

export default AllPatientsTable;